import React, { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { MdOutlineDashboard, MdInventory } from "react-icons/md";
import { RiBox3Line } from "react-icons/ri";
import { BsBoxSeam } from "react-icons/bs";
import { TbReport, TbNotes, TbBuilding } from "react-icons/tb";
import { IoAnalyticsOutline, IoLogOutSharp, IoTrendingDown } from "react-icons/io5";
import { IoIosReturnLeft } from "react-icons/io";
import { HiChevronLeft, HiChevronRight } from "react-icons/hi2";
import JDLogo from '../../assets/JDLogo.png';
import JDSmallLogo from '../../assets/jdSmallLogo.png';
import { Sidebar } from './Sidebar';

const Layout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [hoveredItem, setHoveredItem] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0 });
  const buttonRefs = useRef({});
  
  const navItems = [
    { id: 'dashboard', label: 'Dashboard', icon: MdOutlineDashboard, path: '/' },
    { id: 'company', label: 'Company Management', icon: TbBuilding, path: '/company' },
    { id: 'products', label: 'Product Master', icon: RiBox3Line, path: '/products' },
    { id: 'inward', label: 'Inward Management', icon: IoTrendingDown, path: '/inward' },
    { id: 'outward', label: 'Outward Management', icon: IoAnalyticsOutline, path: '/outward' },
    { id: 'returns', label: 'Returns Management', icon: IoIosReturnLeft, path: '/returns' },
    { id: 'putaway', label: 'Putaway Management', icon: TbNotes, path: '/putaway' },
    { id: 'bins', label: 'Bin Management', icon: BsBoxSeam, path: '/bins' },
    { id: 'inventory', label: 'Inventory Management', icon: MdInventory, path: '/inventory' },
    { id: 'reports', label: 'Reports', icon: TbReport, path: '/reports' },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'row', height: '100vh', width: '100vw', backgroundColor: '#f9fafb', overflow: 'hidden', position: 'relative' }}>
      {/* Mobile Overlay */}
      {isSidebarOpen && (
        <div 
          className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-20"
          onClick={() => setIsSidebarOpen(false)}
          style={{ zIndex: 20 }}
        />
      )}
      {/* Left Sidebar */}
      <Sidebar
        isSidebarOpen={isSidebarOpen}
        setIsSidebarOpen={setIsSidebarOpen}
        hoveredItem={hoveredItem}
        setHoveredItem={setHoveredItem}
        tooltipPos={tooltipPos}
        setTooltipPos={setTooltipPos}
        buttonRefs={buttonRefs}
        navItems={navItems}
        location={location}
        navigate={navigate}
      />
      {/* Main Content Area */}
      <main className="flex-1 flex flex-col overflow-hidden md:ml-5 md:mt-5 md:mb-5 relative z-10" style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', marginLeft: '20px', marginTop: '20px', marginBottom: '20px', position: 'relative', zIndex: 1 }}>
        {/* Header */}
        <header className="w-full md:h-[72px] h-auto bg-white border border-[#F5EFEB66] rounded-[28px] md:px-20 px-4 py-3 md:py-4 flex justify-between items-center shadow-sm flex-shrink-0" style={{ 
          width: '100%', 
          height: '72px', 
          backgroundColor: '#ffffff', 
          border: '0.5px solid #F5EFEB66',
          borderRadius: '28px', 
          padding: '16px 80px', 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)', 
          flexShrink: 0 
        }}>
          {/* Left Side: Pill Title */}
          <div className="md:block hidden" style={{
            height: '36px',
            paddingLeft: '16px',
            paddingRight: '16px',
            border: '1px solid #E5E7EB',
            borderRadius: '9999px',
            backgroundColor: '#ffffff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginLeft: '-50px'
          }}>
            <span style={{
              color: '#111827',
              fontSize: '16px',
              fontWeight: '600'
            }}>
              WMS Portal
            </span>
          </div>
          <div className="md:hidden block">
            <button
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              className="p-2 rounded-lg hover:bg-gray-100"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>

          {/* Right Side: User Info */}
          <div className="flex items-center gap-2 md:gap-3">
            <div style={{ 
              width: '40px', 
              height: '40px', 
              background: 'linear-gradient(to bottom right, #60a5fa, #2563eb)', 
              borderRadius: '50%', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              color: '#ffffff', 
              fontSize: '16px', 
              fontWeight: '600',
              border: '1px solid #E5E7EB',
              boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
            }}>
              U
            </div>
            <div className="hidden md:flex flex-col" style={{ display: 'flex', flexDirection: 'column', lineHeight: '1.25' }}>
              <p style={{ 
                fontSize: '16px', 
                fontWeight: '600', 
                color: '#111827', 
                margin: 0 
              }}>
                User
              </p>
              <p style={{ 
                fontSize: '14px', 
                fontWeight: '500', 
                color: '#9CA3AF', 
                margin: 0 
              }}>
                Admin Account
              </p>
            </div>
          </div>
        </header>

        {/* Page Content - overflow auto so content can scroll when sidebar open */}
        <div className="flex-1 overflow-auto p-2 md:p-4 bg-[#f9fafb] min-w-0" style={{ flex: 1, overflow: 'auto', padding: '1rem', backgroundColor: '#f9fafb', minWidth: 0 }}>
          {children}
        </div>
      </main>
    </div>
  );
};

export default Layout;